import type { VParcelaTecnica } from '@aosenuma/lib-types';

/**
 * Resuelve el texto del input "Número o ID" del wizard de Captura a un
 * `parcela_id` canónico (HUM-PNNNN / COR-PNNNN), considerando un filtro
 * opcional de ejido para desambiguar números repetidos entre núcleos.
 *
 * Verificación de datos previa (ver docs/handoff-captura-ejido-filter-2026-05-08.md §1):
 *   - 2,374 parcelas en 14 ejidos del tenant Elawan.
 *   - El par (nom_nucleo, num_parcela) es único en 13/14 ejidos.
 *   - Única colisión interna documentada: PURISIMA DE SAN RAFAEL · 47
 *     → COR-P0586 + COR-P0587 (viene del RAN tal cual; no se "arregla" en datos).
 *   - 22 parcelas sin num_parcela; solo se resuelven por ID/sufijo.
 *
 * El filtro por ejido cubre ~99.96% de los casos. La rama `ambiguous` es
 * NECESARIA, no opcional — cubre la colisión PURISIMA-47 y el caso "el
 * técnico no seleccionó ejido pero el número está repetido entre núcleos".
 */

/** Resultado del resolver. Discriminated union — el caller debe manejar las 3 ramas. */
export type ResolveResult =
  | { kind: 'ok'; parcela_id: string }
  | { kind: 'not_found'; reason: string }
  | { kind: 'ambiguous'; candidates: ParcelaCandidate[] };

/** Subconjunto de campos de VParcelaTecnica que la UI de ambigüedad necesita. */
export interface ParcelaCandidate {
  parcela_id: string;
  /** null para tierras de uso común (HUM-UC#### no tiene número RAN, mig 0068). */
  num_parcela: number | null;
  nom_nucleo: string;
  municipio: string;
  area_ha: number;
  titular_masked: string | null;
}

/** Filtro de ejido. `municipio` se compara case-insensitive (la DB lo guarda UPPERCASE). */
export interface EjidoFilter {
  nom_nucleo: string;
  municipio: string;
}

/** ID completo: parcela individual (HUM-P####) o uso común (HUM-UC####, mig 0068). */
const RE_PARCELA_ID_FULL = /^(HUM|COR)-(P|UC)\d{4}$/;
const RE_DIGITS = /^\d+$/;
/** Sufijo tipo "P1114" / "P0042". Mismo patrón que la rama legacy. */
const RE_SUFFIX = /^P\d{1,4}$/;
/** Sufijo de uso común: "UC0001" o "UC1" (se normaliza a 4 dígitos). */
const RE_SUFFIX_UC = /^UC0*(\d{1,4})$/;

function toCandidate(p: VParcelaTecnica): ParcelaCandidate | null {
  // Uso común no tiene num_parcela — sigue siendo candidato válido (la UI
  // titula con tituloParcelaCampo, que entiende los IDs HUM-UC####).
  const esUsoComun = /-UC\d{4}$/.test(p.parcela_id);
  if ((p.num_parcela == null && !esUsoComun) || !p.nom_nucleo) return null;
  return {
    parcela_id: p.parcela_id,
    num_parcela: p.num_parcela,
    nom_nucleo: p.nom_nucleo,
    municipio: p.municipio,
    area_ha: p.area_ha,
    titular_masked: p.titular_masked,
  };
}

export function resolveParcelaInput(
  raw: string,
  parcelas: ReadonlyArray<VParcelaTecnica>,
  ejidoFilter?: EjidoFilter | null,
): ResolveResult {
  const q = raw.trim().toUpperCase();
  if (!q) return { kind: 'not_found', reason: '' };

  // ─── Caso 1: ID completo HUM-PNNNN / COR-PNNNN / HUM-UCNNNN ─────────
  // El ID público ya es único — ignora el filtro de ejido. Si el técnico
  // copió un ID exacto y este no coincide con su ejido seleccionado, gana
  // el ID (consistente con v_parcelas_tecnicas.parcela_id como UNIQUE).
  if (RE_PARCELA_ID_FULL.test(q)) {
    const found = parcelas.find((p) => p.parcela_id === q);
    if (found) return { kind: 'ok', parcela_id: found.parcela_id };
    const esUsoComun = q.includes('-UC');
    return {
      kind: 'not_found',
      reason: esUsoComun
        ? `No existe el área de uso común ${q}. Verifica el ID en el mapa (capa "Uso común") o que el seed db:load-uso-comun haya corrido.`
        : `No existe la parcela ${q}`,
    };
  }

  // ─── Caso 2: solo dígitos ───────────────────────────────────────────
  // Aquí entra la lógica del filtro. SIN filtro y >1 match → ambiguous;
  // CON filtro restringimos a ese ejido y, si igual hay >1 (caso PURISIMA-47),
  // también devolvemos ambiguous para que la UI muestre lista clickable.
  if (RE_DIGITS.test(q)) {
    const num = parseInt(q, 10);
    if (!Number.isFinite(num)) {
      return { kind: 'not_found', reason: `Número inválido: ${q}` };
    }

    let matches = parcelas.filter((p) => p.num_parcela === num);

    if (ejidoFilter) {
      // Comparación case-insensitive defensiva: la DB normaliza municipio
      // a UPPERCASE en v_parcelas_tecnicas (ver migration 0035), pero
      // `nom_nucleo` puede venir mixed case. Usamos toUpperCase para
      // ambos lados sin riesgo.
      const fNuc = ejidoFilter.nom_nucleo.toUpperCase();
      const fMun = ejidoFilter.municipio.toUpperCase();
      matches = matches.filter(
        (p) =>
          (p.nom_nucleo ?? '').toUpperCase() === fNuc &&
          p.municipio.toUpperCase() === fMun,
      );
      if (matches.length === 0) {
        return {
          kind: 'not_found',
          reason: `No hay parcela ${num} en ${ejidoFilter.nom_nucleo} (${ejidoFilter.municipio})`,
        };
      }
    } else if (matches.length === 0) {
      return { kind: 'not_found', reason: `No se encontró parcela número ${num}` };
    }

    if (matches.length === 1) {
      return { kind: 'ok', parcela_id: matches[0]!.parcela_id };
    }

    // >1 → ambigüedad. Construye candidatos con todos los campos que la UI
    // necesita para que el técnico decida.
    const candidates = matches
      .map(toCandidate)
      .filter((c): c is ParcelaCandidate => c != null)
      .sort(
        (a, b) =>
          a.municipio.localeCompare(b.municipio) ||
          a.nom_nucleo.localeCompare(b.nom_nucleo, 'es-MX') ||
          a.parcela_id.localeCompare(b.parcela_id),
      );
    return { kind: 'ambiguous', candidates };
  }

  // ─── Caso 3: sufijo P1114 ───────────────────────────────────────────
  // El sufijo es discriminante (último 4 dígitos del parcela_id). Ignora
  // ejido — el ID pseudo-completo ya identifica la parcela.
  if (RE_SUFFIX.test(q)) {
    const matches = parcelas.filter((p) => p.parcela_id.endsWith(`-${q}`));
    if (matches.length === 1) return { kind: 'ok', parcela_id: matches[0]!.parcela_id };
    if (matches.length === 0) return { kind: 'not_found', reason: `No se encontró parcela ${q}` };
    // Improbable: P0042 podría existir en HUM y COR (sufijo igual entre municipios).
    // Fallback a ambiguous para que el técnico escoja.
    const candidates = matches
      .map(toCandidate)
      .filter((c): c is ParcelaCandidate => c != null);
    return { kind: 'ambiguous', candidates };
  }

  // ─── Caso 4: sufijo de uso común UC0001 / UC1 (mig 0068) ───────────
  // Se normaliza a 4 dígitos (el ID canónico siempre es -UC####). Como las
  // áreas de uso común NO tienen número RAN, este sufijo y el ID completo
  // son las únicas formas de teclearlas; típico flujo: clic en mapa.
  const ucMatch = RE_SUFFIX_UC.exec(q);
  if (ucMatch?.[1]) {
    const canonical = `UC${ucMatch[1].padStart(4, '0')}`;
    const matches = parcelas.filter((p) => p.parcela_id.endsWith(`-${canonical}`));
    if (matches.length === 1) return { kind: 'ok', parcela_id: matches[0]!.parcela_id };
    if (matches.length === 0) {
      return {
        kind: 'not_found',
        reason: `No se encontró el área de uso común ${canonical}. Las áreas de uso común no tienen número RAN: usa el ID completo (HUM-UC0001) o selecciónala en el mapa.`,
      };
    }
    // HUM-UC0001 y COR-UC0001 comparten sufijo — lista para que escoja.
    const candidates = matches
      .map(toCandidate)
      .filter((c): c is ParcelaCandidate => c != null);
    return { kind: 'ambiguous', candidates };
  }

  // ─── Sin match: ningún formato reconocido ──────────────────────────
  return {
    kind: 'not_found',
    reason: `Formato no reconocido: "${raw.trim()}". Usá el ID completo (HUM-PNNNN), un número (70), un sufijo (P1114) o un uso común (HUM-UC0001 / UC0001).`,
  };
}
